/**
 * Helpers for Customers factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { Customer } from "le-coffre-resources/dist/Notary";
import type { ApiPayload, QueryPayload } from "../../../../types";
import type { IGetCustomersparams, IPutCustomersParams } from "../Customers";
import {
	buildCustomersCreatePayload,
	buildCustomersUpdatePayload,
	buildDocumentsUidPayload,
	buildEmptyPayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

/**
 * Create GET method for customers
 */
export function createGetMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (q: IGetCustomersparams): Promise<Customer[]> => {
		const url = new URL(baseUrl);
		baseNotary.appendJsonSearchParams(url, { q });
		try {
			return await baseNotary.getRequest<Customer[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for customers
 */
export function createPostMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (body: ApiPayload): Promise<Customer> => {
		const url = new URL(baseUrl);
		try {
			const payload = buildCustomersCreatePayload(body);
			return await baseNotary.postRequest<Customer>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create GET by UID method for customers
 */
export function createGetByUidMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<Customer> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const query = { q };
		if (q) baseNotary.appendJsonSearchParams(url, query);
		try {
			return await baseNotary.getRequest<Customer>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method for customers
 */
export function createPutMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, body: IPutCustomersParams): Promise<Customer> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildCustomersUpdatePayload(body);
			return await baseNotary.putRequest<Customer>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create send reminder method
 */
export function createSendReminderMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, documentsUid: string[]): Promise<void> => {
		const url = new URL(baseUrl.concat(`/${uid}/send_reminder`));
		try {
			const payload = buildDocumentsUidPayload(documentsUid);
			await baseNotary.postRequest<void>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create resend invitation method
 */
export function createResendInvitationMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<void> => {
		const url = new URL(baseUrl.concat(`/${uid}/resend-invitation`));
		try {
			await baseNotary.postRequest<void>(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create send login code method (generate and send OTP SMS, return code for notary display)
 */
export function createSendLoginCodeMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<{ message: string; code: string }> => {
		const url = new URL(baseUrl.concat(`/${uid}/send-login-code`));
		try {
			return await baseNotary.postRequest<{ message: string; code: string }>(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}
